import * as React from 'react';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { Alert, Button, DatePicker, Layout, List, message, Popconfirm } from 'antd';
import { Link } from 'react-router-dom';
import DetailPageHeader from '../detail_page_header/DetailPageHeader';
import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import {
	projectsRestCrudThunksForBuyer,
	projectsRestCrudThunksForSupplier,
} from '../../thunks/projects_thunks';
import { ROLE_TYPES } from '../../utils/DataConstants';
import { PageLoadingPlaceholder } from '../page_loading_placeholder/PageLoadingPlaceholder';
import DeleteConfirmButton from '../delete_pop_confirm/DeletePopConfirm';
import moment from 'moment';
import ProjectStatusDisplay from '../project_status_display/ProjectStatusDisplay';
import { PROJECT_ENTITY_TYPES } from '../../constants/ProjectEntities';
import { isProjectsV2Enabled } from '../../utils/FeatureFlagUtils';

const queryString = require('qs');
const ListItem = List.Item;
const ListItemMeta = ListItem.Meta;
const { Content } = Layout;
const { RangePicker } = DatePicker;
const dateFormat = 'MM-DD-YYYY';

class ProjectsDetailPage extends React.Component<any, any> {
	formRefs: any;

	state = {
		loading: true,
		reopenModalVisible: false,
		cancelModalVisible: false,
		backLinkTo: '/buyer/projects',
		buttonLoading: false,
	};

	constructor(props) {
		super(props);
		this.formRefs = {};
	}

	componentDidMount() {
		const { getProject, location, match, currentUser, userType } = this.props;
		const projectId = match.params.id;
		getProject(userType, projectId).then((project) => {
			let searchString = location.search;
			if (searchString[0] === '?') {
				searchString = searchString.slice(1);
			}
			const queryParams = queryString.parse(searchString);
			if (queryParams.backlinkParams) {
				const { tab, pathName, backLinkText, ...otherParams } = queryString.parse(
					queryParams.backlinkParams
				);
				const finalPath = pathName || `/${userType}/projects/overview/${tab ? tab : 'active'}`;
				this.setState({
					loading: false,
					backLinkTo: `${finalPath}?${new URLSearchParams(otherParams).toString()}`,
				});
			} else {
				this.setState({
					loading: false,
					backLinkTo: `/${userType}/projects/overview/`,
				});
			}
		});
	}

	saveFormRef = (formName) => (formRef) => {
		this.formRefs[formName] = formRef;
	};

	handleTabChange = (key) => {
		const { history, match, currentUser, userType } = this.props;
		const projectId = match.params.id;
		history.push(`/${userType}/projects/detail/${projectId}/${key}`);
	};

	checkTabActive = (key) => {
		const { history } = this.props;
		const pathname = history.location.pathname;
		return pathname.indexOf(key) !== -1;
	};

	handleDelete = () => {
		const { history, deleteProject, project, currentUser, userType } = this.props;
		this.setState({ buttonLoading: true });
		deleteProject(userType, project)
			.then(() => {
				history.push(this.state.backLinkTo);
			})
			.catch((e) => {
				message.error(`Failed to delete project.`);
			})
			.finally(() => {
				this.setState({ buttonLoading: false });
			});
	};

	handleArchive = () => {
		const { history, archiveProject, project, currentUser, userType } = this.props;
		this.setState({ buttonLoading: true });
		archiveProject(userType, project)
			.then((e) => {
				history.push(this.state.backLinkTo);
			})
			.catch((e) => {
				message.error(`Failed to archive project.`);
			})
			.finally(() => {
				this.setState({ buttonLoading: false });
			});
	};

	handleUnarchive = () => {
		const { history, unarchiveProject, project, currentUser, userType } = this.props;
		this.setState({ buttonLoading: true });
		unarchiveProject(userType, project)
			.then(() => {
				history.push(this.state.backLinkTo);
			})
			.catch((e) => {
				message.error(`Failed to unarchive project.`);
			})
			.finally(() => {
				this.setState({ buttonLoading: false });
			});
	};

	render() {
		const { project, match, currentUser, companyConfig, userType, updateErrors } = this.props;
		const { loading, buttonLoading } = this.state;
		const projectsId = match.params.id;

		const entities = nullSafeGetOrElse('entities', project, []);
		// projects-v2: deliverables/tasks tabs + header status/dates gated on the flag.
		const projectsV2 = isProjectsV2Enabled();
		const tabs = [
			{
				key: 'details',
				name: 'Overview',
			},
			// Deliverables tab only when the project has the deliverables entity.
			...(projectsV2 && entities.includes(PROJECT_ENTITY_TYPES.deliverables)
				? [
						{
							key: 'deliverables',
							name: 'Deliverables',
						},
				  ]
				: []),
			// Tasks tab only when the project has the tasks entity.
			...(projectsV2 && entities.includes(PROJECT_ENTITY_TYPES.tasks)
				? [
						{
							key: 'tasks',
							name: 'Tasks',
						},
				  ]
				: []),
			{
				key: 'workOrders',
				name: 'Work Orders',
			},
			{
				key: 'invoices',
				name: 'Invoices',
			},
			// projects-v2: demo notes tab.
			...(projectsV2 ? [{ key: 'notes', name: 'Notes' }] : []),
		];

		// Status + date range come from the server-backed project record.
		const projectStatus = nullSafeGet('status', project);
		const startDate = nullSafeGet('startDate', project);
		const endDate = nullSafeGetOrElse('endDate', project, nullSafeGet('dueDate', project));
		// Show whatever dates exist (End Date is optional), matching the V2 overview's
		// start||end with em-dash placeholders — an end-only gate hid start-only projects.
		const dateRange =
			startDate || endDate
				? `${startDate ? moment(startDate).format('MMM D') : '—'} - ${
						endDate ? moment(endDate).format('MMM D') : '—'
				  }`
				: null;

		const projectActions = () => {
			let actions = [];
			actions.push(
				<Link
					to={`/${userType}/projects/edit/${projectsId}`}
					style={{ marginRight: 16, marginBottom: 16 }}
				>
					<Button loading={buttonLoading} ghost={true}>
						Edit
					</Button>
				</Link>
			);
			actions.push(
				<DeleteConfirmButton
					onConfirm={this.handleDelete}
					title={'Are you sure you want to delete this project?'}
				>
					<Button
						loading={buttonLoading}
						ghost={true}
						style={{ marginBottom: 16, marginRight: 16 }}
						key={'Delete'}
					>
						Delete
					</Button>
				</DeleteConfirmButton>
			);
			if (!project.isArchived) {
				actions.push(
					<Button
						loading={buttonLoading}
						ghost={true}
						style={{ marginBottom: 16, marginRight: 16 }}
						key={'Archive'}
						onClick={this.handleArchive}
					>
						Archive
					</Button>
				);
			} else {
				actions.push(
					<Button
						loading={buttonLoading}
						ghost={true}
						style={{ marginBottom: 16, marginRight: 16 }}
						key={'Archive'}
						onClick={this.handleUnarchive}
					>
						Unarchive
					</Button>
				);
			}
			return actions;
		};

		return loading ? (
			<PageLoadingPlaceholder />
		) : (
			<div className="workOrdersDetailPage">
				{updateErrors.length > 0 ? <Alert message={updateErrors.join(' ')} type="error" /> : null}
				<DetailPageHeader
					exactPath={`/${userType}/projects/detail/:id/details`}
					redirectPath={`/${userType}/projects/detail/${projectsId}/details`}
					backLinkText={'Back to projects'}
					backLinkTo={this.state.backLinkTo}
					title={
						loading ? null : (
							<div
								style={{
									display: 'flex',
									flexDirection: 'row',
									justifyContent: 'flex-start',
									alignItems: 'baseline',
									gap: '16px',
									flexWrap: 'nowrap',
								}}
							>
								<div
									style={{
										display: 'flex',
										marginBottom: '-8px',
									}}
								>
									{nullSafeGet('title', project)}
								</div>
								{projectsV2 && projectStatus ? (
									<ProjectStatusDisplay status={projectStatus} />
								) : null}
							</div>
						)
					}
					subtitle={<div></div>}
					subtext={projectsV2 && dateRange ? <div>{dateRange}</div> : <div></div>}
					actions={projectActions()}
					tabs={tabs}
					handleTabChange={this.handleTabChange}
					checkTabActive={this.checkTabActive}
					childRoutes={this.props.childRoutes}
					backgroundColor={nullSafeGet('detail.config.theme.navblock', companyConfig)}
				/>
			</div>
		);
	}
}

const mapStateToProps = (state, ownProps) => ({
	createErrors: state.projects.createErrors,
	updateErrors: state.projects.updateErrors,
	project: state.projects.detail,
	match: ownProps.match,
	history: ownProps.history,
	currentUser: state.session.currentUser,
	companyConfig: state.company_config,
});

const mapDispatchToProps = (dispatch) => ({
	getProject: (userType, id) =>
		dispatch(
			userType === ROLE_TYPES.SUPPLIER
				? projectsRestCrudThunksForSupplier.readOne(id)
				: projectsRestCrudThunksForBuyer.readOne(id)
		),
	deleteProject: (userType, entity) =>
		dispatch(
			userType === ROLE_TYPES.SUPPLIER
				? projectsRestCrudThunksForSupplier.delete(entity, 'id', null, true)
				: projectsRestCrudThunksForBuyer.delete(entity, 'id', null, true)
		),
	archiveProject: (userType, entity) =>
		dispatch(
			userType === ROLE_TYPES.SUPPLIER
				? projectsRestCrudThunksForSupplier.archive(entity, 'id', true)
				: projectsRestCrudThunksForBuyer.archive(entity, 'id', true)
		),
	unarchiveProject: (userType, entity) =>
		dispatch(
			userType === ROLE_TYPES.SUPPLIER
				? projectsRestCrudThunksForSupplier.unarchive(entity, 'id', true)
				: projectsRestCrudThunksForBuyer.unarchive(entity, 'id', true)
		),
});
const ComponentWithoutUserType = withRouter(
	connect(mapStateToProps, mapDispatchToProps)(ProjectsDetailPage)
);

export default connect(
	(state) => ({
		userType: (state as any).session.userType,
	}),
	() => ({})
)(ComponentWithoutUserType);
